import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Save } from 'lucide-react';
import { Button } from '../ui/Button';
import { FormField } from '../ui/FormField';
import { Alert } from '../ui/Alert';
import { invoiceService } from '../../lib/database/invoices';
import { clientService } from '../../lib/database/clients';
import { useAuth } from '../../hooks/useAuth';

interface InvoiceItem {
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

interface CreateInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateInvoiceModal: React.FC<CreateInvoiceModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [clients, setClients] = useState<any[]>([]);

  // Form state
  const [clientId, setClientId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [taxRate, setTaxRate] = useState(18);
  const [items, setItems] = useState<InvoiceItem[]>([
    { description: '', quantity: 1, rate: 0, amount: 0 }
  ]);

  // Load clients on mount
  useEffect(() => {
    const loadClients = async () => {
      try {
        const { data } = await clientService.getClients();
        if (data) {
          setClients(data);
        }
      } catch (error) {
        console.error('Error loading clients:', error);
      }
    };

    if (isOpen) {
      loadClients();
    }
  }, [isOpen]);

  // Calculate totals
  const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
  const taxAmount = (subtotal * taxRate) / 100;
  const total = subtotal + taxAmount;

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, rate: 0, amount: 0 }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index: number, field: keyof InvoiceItem, value: string | number) => {
    const updatedItems = [...items];
    
    // Handle quantity as integer only
    if (field === 'quantity') {
      const intValue = parseInt(value.toString()) || 1;
      updatedItems[index] = { ...updatedItems[index], [field]: intValue };
      updatedItems[index].amount = intValue * updatedItems[index].rate;
    } else {
      updatedItems[index] = { ...updatedItems[index], [field]: value };
      
      // Recalculate amount when rate changes
      if (field === 'rate') {
        updatedItems[index].amount = updatedItems[index].quantity * Number(value);
      }
    }
    
    setItems(updatedItems);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validation
      if (!clientId) {
        setError('Please select a client');
        return;
      }

      if (!dueDate) {
        setError('Please set a due date');
        return;
      }

      if (items.some(item => !item.description.trim())) {
        setError('Please fill in all item descriptions');
        return;
      }

      if (items.some(item => item.rate <= 0)) {
        setError('All items must have a rate greater than 0');
        return;
      }

      if (items.some(item => item.quantity <= 0)) {
        setError('All items must have a quantity greater than 0');
        return;
      }

      // Create invoice
      const { data: invoice, error: invoiceError } = await invoiceService.createInvoice({
        client_id: clientId,
        due_date: dueDate,
        subtotal,
        tax_rate: taxRate,
        tax_amount: taxAmount,
        total,
        notes: notes.trim() || null,
        created_by: user?.id || null,
      });

      if (invoiceError || !invoice) {
        setError('Failed to create invoice: ' + (invoiceError?.message || 'Unknown error'));
        return;
      }

      // Create invoice items
      const invoiceItems = items.map(item => ({
        invoice_id: invoice.id,
        description: item.description.trim(),
        quantity: item.quantity,
        rate: item.rate,
        amount: item.amount,
      }));

      const { error: itemsError } = await invoiceService.createInvoiceItems(invoiceItems);

      if (itemsError) {
        setError('Invoice created but failed to add items: ' + itemsError.message);
        return;
      }

      setSuccess('Invoice created successfully!');
      
      // Reset form
      setTimeout(() => {
        onSuccess();
        onClose();
        resetForm();
      }, 1500);

    } catch (error: any) {
      setError('An unexpected error occurred: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setClientId('');
    setDueDate('');
    setNotes('');
    setTaxRate(18);
    setItems([{ description: '', quantity: 1, rate: 0, amount: 0 }]);
    setError('');
    setSuccess('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Create New Invoice</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && <Alert type="error" message={error} />}
          {success && <Alert type="success" message={success} />}

          {/* Client and Due Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="Client" required>
              <select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">Select a client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name} - {client.company || 'No company'}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Due Date" required>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </FormField>
          </div>

          {/* Tax Rate */}
          <FormField label="Tax Rate (%)" helpText="Default is 18% GST">
            <input
              type="number"
              value={taxRate}
              onChange={(e) => setTaxRate(Number(e.target.value))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              min="0"
              max="100"
              step="0.01"
            />
          </FormField>

          {/* Invoice Items */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Invoice Items</h3>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                icon={Plus}
                onClick={addItem}
              >
                Add Item
              </Button>
            </div>

            <div className="space-y-4">
              {items.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-12 md:col-span-5">
                    <FormField label="Description" required>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="Item description"
                        required
                      />
                    </FormField>
                  </div>

                  <div className="col-span-4 md:col-span-2">
                    <FormField label="Qty" required>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        min="1"
                        step="1"
                        required
                      />
                    </FormField>
                  </div>

                  <div className="col-span-4 md:col-span-2">
                    <FormField label="Rate (₹)" required>
                      <input
                        type="number"
                        value={item.rate}
                        onChange={(e) => updateItem(index, 'rate', Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        min="0.01"
                        step="0.01"
                        required
                      />
                    </FormField>
                  </div>

                  <div className="col-span-3 md:col-span-2">
                    <FormField label="Amount (₹)">
                      <input
                        type="text"
                        value={`₹${item.amount.toFixed(2)}`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                        readOnly
                      />
                    </FormField>
                  </div>

                  <div className="col-span-1">
                    <Button
                      type="button"
                      variant="error"
                      size="sm"
                      icon={Trash2}
                      onClick={() => removeItem(index)}
                      disabled={items.length === 1}
                      className="w-full"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">₹{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tax ({taxRate}%):</span>
                <span className="font-medium">₹{taxAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Total:</span>
                <span>₹{total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <FormField label="Notes" helpText="Optional notes for the invoice">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows={3}
              placeholder="Additional notes or terms..."
            />
          </FormField>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              icon={Save}
              loading={loading}
              disabled={loading}
            >
              Create Invoice
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};